/**
 * Shrinks a photo before it's uploaded (2026-10-09).
 *
 * A phone photo of a bill is 3 to 5 MB. Uploading that on a phone is slow,
 * and reading it was what made the first look take a minute. 1600px on
 * the long side is plenty to read a bill, and the server shrinks again
 * before the model sees it anyway. PDFs and small images come back as is.
 * Anything that goes wrong (odd format, no canvas) returns the original.
 */
const MAX_SIDE = 1600;
const QUALITY = 0.85;
const SKIP_UNDER_BYTES = 600 * 1024;

export async function shrinkImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.size < SKIP_UNDER_BYTES) return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
    if (!blob || blob.size >= file.size) return file;
    const name = file.name.replace(/\.(png|jpe?g|heic|heif|webp)$/i, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  }
}
